
import { Dictionary } from '@ngrx/entity';
import { createSelector } from '@ngrx/store';
import { capitalize } from '../../helpers/helpers';
import { flatten, sortByProp, getSet, str2Date, HHMMToMs, makeMapFromListOfObjectsAndKey, setIntersection, eventDateStringJoiner } from 'globals/helpers/helpers';
import { EventListTableData } from 'src/app/helpers/table-column-types';
import { publishDotMenuIds, tableLightColors } from 'src/app/helpers/table-helpers';
import { PublicEventData } from 'globals/store/public.model';
import { JoiAccount } from 'globals/store/accounts.data';
import { JoiEvent, Label } from 'globals/store/events.data';
import { PublishInfo } from 'globals/store/share.data';
import { accountListSelector, activeAccountListReselector, getAccountEventLabels } from './accounts.reselector';
import { eventListReselector } from './events.reselector';
import { sortEvents } from './filteredEvents.reselector';
import { EventListPublish, EventListPublishInstance, EventListsData, InstanceInfo } from 'globals/store/share.event-lists.data';
import { environment } from 'src/environments/environment';
import { extractEventListPublishCalendarData, programPublishToEventListPublish } from '../share.event-lists.functions';
import { filterStateSelector } from '../filters.era';
import { accountStateSelector } from '../accounts.era';
import { eventStateSelector } from '../events.era';
import { routerStateSelector } from '../router.state';
import { shareStateSelector } from '../share.era';
import { getEventListPublishInstance } from 'globals/helpers/share.event-list.helper';

const eventListSearchTerm = createSelector(filterStateSelector, (state) => state.eventList.search);
const accounts = createSelector(accountStateSelector, (state) => state.entities);
const account = createSelector(accountStateSelector, (state) => state.account);
const events = createSelector(eventStateSelector, (state) => state.entities);
const url = createSelector(routerStateSelector, (state) => state.state.url);
const publishInfos = createSelector(shareStateSelector, (state) => state.publish);
const publishedData = createSelector(shareStateSelector, (state) => state.published_data);

export interface EventListsSelectionsData {
  eventId: string
  title: string
  dates: string
  totalSelected: number
  labels: string[]
  instances: InstanceInfo[]
  publishInfos: PublishInfo[]
  nInfos: number
}


export interface EventListSelectionsReselectorData {
  accountLabels: { [id: string]: Label }
  eventListSelections: EventListsSelectionsData[]
}

const createEventListSelections = (url: string, accounts: Dictionary<JoiAccount>, eventList: JoiEvent[], publishInfos: Dictionary<PublishInfo>, { accountId, eventListInfoId }: { accountId: string, eventListInfoId: string }): EventListSelectionsReselectorData => {
  if(!/\/event-lists/.test(url)) return null // only designed to work on event lists

  const accountEvents = eventList.filter(e => e.accountId === accountId).filter(e => !e.closed)
  const publishInfosByEventId: { [eventId:string]: PublishInfo[] } = {}
  const eventIds = accountEvents.map(e => e.eventId)
  eventIds.forEach(eventId => publishInfosByEventId[eventId] = [])
  const publishInfosList = Object.values(publishInfos).filter(i => i.status === 'published' && i.accountId === accountId && eventIds.includes(i.eventId))
  const publishIds = getSet(publishInfosList, 'publishCode')
  publishInfosList.forEach(i => publishInfosByEventId[i.eventId].push(i))
  

  eventIds.forEach(eventId => publishInfosByEventId[eventId].sort(sortByProp('title')))
  // console.log({ publishInfos: jsonCopyObject(publishInfos), publishInfosList: jsonCopyObject(publishInfosList), accountId, eventIds, publishInfosByEventId })
  const accountLabels = getAccountEventLabels(accounts[accountId]).accountEventLabelsById

  const { sortOption, eventInstanceMap } = accounts[accountId].eventListInfos[eventListInfoId]
  const { event_order_key, event_order_rev } = sortOption
  
  const sortedEvents = sortEvents(accountEvents, event_order_key, event_order_rev)

  return { accountLabels, eventListSelections: sortedEvents.map(({ eventId, title, dates_string, labels }): EventListsSelectionsData => {
    const instances = !eventInstanceMap[eventId] ? [{ publishIds: [] }] : eventInstanceMap[eventId].map(instance => 
      ({ ...instance, publishIds: instance.publishIds.filter(id => publishIds.has(id)) }) 
    )
    return {
      title,
      eventId,
      dates: dates_string && dates_string.length ? eventDateStringJoiner(dates_string) : 'Date TBA',
      labels,
      instances,
      totalSelected: instances.reduce((total, instance) => total + instance.publishIds.length, 0),
      publishInfos: publishInfosByEventId[eventId],
      nInfos: publishInfosByEventId[eventId].length
    }
  })}
}
export const eventListIdsByPublishIdsReselector = createSelector(
  account,
  (account: JoiAccount) => {
    if (!account || !account.eventListInfos) return {}
    const publishedIdToEventListIds: { [id:string]: Set<string> } = {}
    Object.values(account.eventListInfos).filter(i => i.publishedIds).forEach(i => i.publishedIds.forEach(publishId => {
      if (!publishedIdToEventListIds[publishId]) publishedIdToEventListIds[publishId] = new Set()
      publishedIdToEventListIds[publishId].add(i.publishCode)
    }))
    return publishedIdToEventListIds
  }
)


export const eventListLightsByPublishIdsReselector = createSelector(
  account,
  (account: JoiAccount) => {
    const publishedIdToEventList: { [id:string]: { name: string, published: boolean }[] } = {}
    if (!account || !account.eventListInfos) return {}

    const eventListInfosList = Object.values(account.eventListInfos)
    const publishedEventListInfos = eventListInfosList.filter(i => i.status === 'published')
    const unpublishedEventListInfos = eventListInfosList.filter(i => i.status !== 'published')
    
    unpublishedEventListInfos.forEach(info => {
      const uniquePublishIds = new Set(flatten(Object.values(info.eventInstanceMap).map(instances => flatten(instances.map(instance => instance.publishIds)))))
      uniquePublishIds.forEach(id => {
        if (!publishedIdToEventList[id]) publishedIdToEventList[id] = []
        publishedIdToEventList[id].push({ name: info.title || null, published: false })
      })
    })
    publishedEventListInfos.forEach(info => info.publishedIds.forEach(id => {
      if (!publishedIdToEventList[id]) publishedIdToEventList[id] = []
      publishedIdToEventList[id].push({ name: info.title || null, published: true })
    }))
    return publishedIdToEventList
})

export const eventListsSelectionsReselector = createSelector(
  url,
  accounts,
  eventListReselector,
  publishInfos,
  createEventListSelections
)

export const getEventListData = (account: JoiAccount, eventListInfoId: string, events: { [id:string]: JoiEvent }, eventListSelections: EventListsSelectionsData[], publishedDatas: {[code:string]: PublicEventData }, publishInfos: Dictionary<PublishInfo>): EventListsData => {
  const publishedIds = Object.keys(publishedDatas) || []
  const eventListInfo = account.eventListInfos[eventListInfoId]
  const excluded = eventListInfo ? eventListInfo.excludedFieldIds || [] : []
  // console.log({ excluded, eventListInfo, account, eventListInfoId })

  // console.log({ publishedIds: jsonCopyObject(publishedIds), publishedDatas })

  
  const instances: EventListPublishInstance[] = flatten(eventListSelections.map(ELS => {
    const event = events[ELS.eventId]
    const instances: EventListPublishInstance[]  = ELS.instances.map((instanceInfo, index) => {
      const publishes: EventListPublish[] = instanceInfo.publishIds.filter(id => !!publishedDatas[id]).map(id => programPublishToEventListPublish(id, publishedDatas[id], excluded, environment.joi.shareBase))
      return getEventListPublishInstance(event.title, event.eventId, publishes, eventListInfo, index)
    }).filter(i => !!i)
    return instances
  }))

  return {
    accountId: account.id,
    accountName: excluded.includes('accountName') ? null : account.title,
    accountLogoUrl: excluded.includes('accountName') ? null : account.photoURL,
    instances,
    id: eventListInfoId, // same id for event lists publish and info.  no need to complicate things unnecessarily
    publishCalendarData: excluded.includes('calendar') ? null : extractEventListPublishCalendarData(instances, publishedIds, publishedDatas, publishInfos, excluded),
  }
}

export const eventListsReselector = createSelector( // how nice it would be to be able to combine reselectors with optional args
  url,
  accounts,
  events,
  eventListReselector,
  publishInfos,
  publishedData,
  (url: string, accounts: Dictionary<JoiAccount>, events: { [id: string]: JoiEvent }, eventList: JoiEvent[], publishInfos: Dictionary<PublishInfo>, publishedData: {[code:string]: PublicEventData }, { accountId, eventListInfoId }: { accountId: string, eventListInfoId: string } ): EventListsData => {
    const data = createEventListSelections(url, accounts, eventList, publishInfos, { accountId, eventListInfoId })
    if (!data) return null
    return getEventListData(accounts[accountId], eventListInfoId, events, data.eventListSelections, publishedData, publishInfos)
  }
)

export const eventListInfoReselector = createSelector(
  accounts,
  (accounts: Dictionary<JoiAccount>, { accountId, publishCode }) => {
    const account = accounts[accountId]
    if (!account) return null
    return account.eventListInfos[publishCode] || null
  }
)

export const eventListsTableReselector = createSelector(
  activeAccountListReselector,
  eventListSearchTerm,
  (accountList: JoiAccount[], searchTerm: string) => {
    // console.log({ accountList })
    accountList = accountList.filter(a => a.is_admin || a.is_manager || a.is_exec || a.is_leader)
    const eventListIdToAccountMap: { [id:string]: JoiAccount } = {}
    const allEventListInfos = flatten(accountList.map(a => {
      const eventListInfos = Object.values(a.eventListInfos)
      eventListInfos.forEach(info => eventListIdToAccountMap[info.publishCode] = a)
      return eventListInfos
    }))
    
    searchTerm = searchTerm ? searchTerm.toLocaleUpperCase() : null
    const filteredEventListInfos = !searchTerm ? allEventListInfos :
      allEventListInfos.filter(i => i.title.toLocaleUpperCase().includes(searchTerm))

    const activeDotMenuOptions = new Set(publishDotMenuIds)
    activeDotMenuOptions.delete("duplicate")
    if (status !== 'published') activeDotMenuOptions.delete('update')
    
    const eventListTableData: EventListTableData[] = filteredEventListInfos.map(info => {
      const { publishedAt, publisher, status, title, description, publishCode, creator, createdAt } = info
      const account = eventListIdToAccountMap[publishCode]

      const eventListTableRow: EventListTableData = {
        id: publishCode,
        name: { text: title },
        description: { text: description || '' },
        status: {
          percent: status === 'published' ? 1 : status === 'unpublished' ? 0 : -1,
          lights: [{
            text: capitalize(status),
            color: status === 'published' ? tableLightColors.green  : status === 'unpublished' ? tableLightColors.red  : tableLightColors.gray,
          }]},
        created: !createdAt ? null : { updater: creator, lastUpdated: new Date(createdAt) },
        updated: !publishedAt ? null : { updater: publisher, lastUpdated: publishedAt },
        eventListInfo: info,
        account: { name: account.title, photoUrl: account.photoURL || null, id: account.id },
        activeDotMenuOptions
      }
      return eventListTableRow
    })
    const eventListTableRowsById = makeMapFromListOfObjectsAndKey(eventListTableData, 'id')
    return { eventListTableData, eventListTableRowsById, eventListsCountBeforeFiltering: allEventListInfos.length }
  }
)
